import React, { useState, useEffect, useRef } from "react";
import { uniqBy } from "lodash";

import { RotaDropdownHeader, RotaMultiSelectMenu } from "@teamrota/rota-design";
import { Role } from "@teamrota/authlib";

import { SHIFT_SOURCES } from "~/src/consts";
import useAuth from "~/src/auth/hooks/use-auth";
import { useUsers } from "~/src/containers/provide-schedule/use-users";
import { SHIFT_SOURCE_LABELS } from "~/src/containers/provide/components/filters";
import { useHomeVenues } from "~/src/containers/provide-schedule/use-home-venues";

import {
  GET_FILTERS,
  getSelectedIds,
  filterBySearchTerm,
  clearFilteredOptions,
  formatUsers,
  isLastItem
} from "./utils";

import {
  StyledRotaDropdownHeader,
  StyledDropdownsContainer,
  StyledSeparator,
  StyledFilterWrapper,
  StyledChipsContainer
} from "./filter-dropdown.styles";

import FilterChips from "./FilterChips/filter-chips";

import {
  getProvideScheduleFilters,
  setProvideScheduleFilters
} from "../../storage";

const FilterDropdown = ({
  partners,
  handlePartnerSelect,
  setRoleIds,
  setVenueIds,
  setHomeVenueIds,
  setSelectedUserIds,
  isShowCancelledShifts,
  setIsShowCancelledShifts,
  isShowCancelledBookings,
  setIsShowCancelledBookings,
  setShiftTypes,
  setShiftSources,
  setCreatedAfter,
  setAvailabilityWeekDays
}) => {
  const auth = useAuth();
  const isSelfProvider = auth.hasRoles(Role.PROVIDER, Role.REQUESTER);

  // ================= STATE ==================
  const [filters, setFilters] = useState(GET_FILTERS(isSelfProvider).FILTERS);

  const [openDropdowns, setOpenDropdowns] = useState(
    GET_FILTERS(isSelfProvider).DROPDOWNS
  );

  const chipsContainerRef = useRef();

  const { users, isUsersLoading } = useUsers();

  const currentUserId = auth.info.id;

  // ================= COMPUTED_STATE ==================
  const selectedPartnersIds = getSelectedIds("partnerFilter", filters);
  const selectedRolesIds = getSelectedIds("roleFilter", filters);
  const selectedVenuesIds = getSelectedIds("venueFilter", filters);
  const selectedHomeVenuesIds = getSelectedIds("homeVenueFilter", filters);
  const selectedUsersIds = getSelectedIds("createdByFilter", filters);
  const selectedShiftStatusIds = getSelectedIds("shiftStatusFilter", filters);
  const selectedShiftTypes = getSelectedIds("shiftTypesFilter", filters);
  const selectedShiftSources = getSelectedIds("shiftSourcesFilter", filters);
  const selectedCreatedAfter = getSelectedIds("createdAfterFilter", filters);
  const selectedWeekDays = getSelectedIds("availabilityFilter", filters);
  // const selectedAvailabilityWeekDays

  const { homeVenues } = useHomeVenues();
  const homeVenueIds = homeVenues.map(venue => venue.id);

  const filtersArray = Object.values(filters);

  const selectedPartners = selectedPartnersIds.length
    ? partners.filter(partner => selectedPartnersIds.includes(partner.id))
    : partners;

  const rolesOfSelectedPartners = uniqBy(
    selectedPartners.map(partner => partner.roleRates).flat(),
    "roleId"
  );

  const venuesOfSelectedPartners = selectedPartners
    .map(partner => partner.venues)
    .flat();

  const isAnyFilterSelected = filtersArray.some(
    filter => filter.selectedItems.length
  );

  // ================= STATE HANDLERS ==================
  // ---- filters ----
  const handleSelect = (selectedItem, filterName) => {
    if (["createdAfterFilter"].includes(filterName)) {
      // single filter handling
      if (getSelectedIds(filterName, filters).includes(selectedItem.value)) {
        setFilters({
          ...filters,
          [filterName]: {
            ...filters[filterName],
            selectedItems: []
          }
        });
      } else {
        setFilters({
          ...filters,
          [filterName]: {
            ...filters[filterName],
            selectedItems: [selectedItem]
          }
        });
      }
    } else {
      // multi filter handling
      if (getSelectedIds(filterName, filters).includes(selectedItem.value)) {
        setFilters({
          ...filters,
          [filterName]: {
            ...filters[filterName],
            selectedItems: filters[filterName].selectedItems.filter(
              item => item.value !== selectedItem.value
            )
          }
        });
      } else {
        setFilters({
          ...filters,
          [filterName]: {
            ...filters[filterName],
            selectedItems: [...filters[filterName].selectedItems, selectedItem]
          }
        });
      }
    }
  };

  const handleSearch = (value, filterName) => {
    setFilters({
      ...filters,
      [filterName]: {
        ...filters[filterName],
        searchTerm: value
      }
    });
  };

  const handleClear = filterName => {
    setFilters({
      ...filters,
      [filterName]: {
        ...filters[filterName],
        selectedItems: []
      }
    });
  };

  // ---- dropdown ----
  const toggleDropdown = name => {
    if (openDropdowns.main === true && name === "main") {
      setOpenDropdowns(GET_FILTERS(isSelfProvider).DROPDOWNS);
    } else {
      setOpenDropdowns({ ...openDropdowns, [name]: !openDropdowns[name] });
    }
  };

  const setDropdownOpen = name => {
    setOpenDropdowns({
      ...GET_FILTERS(isSelfProvider).DROPDOWNS,
      main: true,
      [name]: true
    });
  };

  // ================= DROPDOWN OPTIONS ==================
  const dropdownOptions = {
    partnerFilter: partners
      ?.map(partner => ({
        value: partner.id,
        label: partner.accountName,
        selected: selectedPartnersIds.includes(partner.id)
      }))
      .sort((a, b) =>
        a.label.toLowerCase().localeCompare(b.label.toLowerCase())
      ),

    roleFilter: rolesOfSelectedPartners
      ?.map(role => ({
        value: role.roleId,
        label: role.roleName,
        selected: selectedRolesIds.includes(role.roleId)
      }))
      .sort((a, b) =>
        a.label.toLowerCase().localeCompare(b.label.toLowerCase())
      ),

    venueFilter: venuesOfSelectedPartners
      .filter(venue =>
        isSelfProvider ? homeVenueIds.includes(venue.id) : true
      )
      ?.map(venue => ({
        value: venue.id,
        label: venue.name,
        selected: selectedVenuesIds.includes(venue.id)
      }))
      .sort((a, b) =>
        a.label.toLowerCase().localeCompare(b.label.toLowerCase())
      ),

    homeVenueFilter:
      isSelfProvider &&
      homeVenues
        ?.map(venue => ({
          value: venue.id,
          label: venue.name,
          selected: selectedHomeVenuesIds.includes(venue.id)
        }))
        .sort((a, b) =>
          a.label.toLowerCase().localeCompare(b.label.toLowerCase())
        ),

    createdByFilter: isUsersLoading
      ? []
      : formatUsers(users, currentUserId).map(user => ({
          ...user,
          selected: selectedUsersIds.includes(user.value)
        })),

    createdAfterFilter: [
      {
        value: "HOUR",
        label: "Start of the hour",
        selected: selectedCreatedAfter.includes("HOUR")
      },
      {
        value: "DAY",
        label: "Start of the day",
        selected: selectedCreatedAfter.includes("DAY")
      },
      {
        value: "YESTERDAY",
        label: "Start of yesterday",
        selected: selectedCreatedAfter.includes("YESTERDAY")
      },
      {
        value: "WEEK",
        label: "Start of week",
        selected: selectedCreatedAfter.includes("WEEK")
      }
    ],

    shiftStatusFilter: [
      {
        value: "CANCELLED_SHIFTS",
        label: "Cancelled shifts",
        selected: !!isShowCancelledShifts
      },
      {
        value: "CANCELLED_BOOKINGS",
        label: "Cancelled bookings",
        selected: !!isShowCancelledBookings
      }
    ],

    shiftTypesFilter: [
      {
        value: "DAY",
        label: "Day shifts",
        selected: selectedShiftTypes.includes("DAY")
      },
      {
        value: "NIGHT",
        label: "Night shifts",
        selected: selectedShiftTypes.includes("NIGHT")
      }
    ],

    shiftSourcesFilter: [
      {
        value: SHIFT_SOURCES.REQUESTED,
        label: SHIFT_SOURCE_LABELS[SHIFT_SOURCES.REQUESTED],
        selected: selectedShiftSources.includes(SHIFT_SOURCES.REQUESTED)
      },
      {
        value: SHIFT_SOURCES.PROVIDED,
        label: SHIFT_SOURCE_LABELS[SHIFT_SOURCES.PROVIDED],
        selected: selectedShiftSources.includes(SHIFT_SOURCES.PROVIDED)
      },
      {
        value: SHIFT_SOURCES.SHIFTSTREAM,
        label: SHIFT_SOURCE_LABELS[SHIFT_SOURCES.SHIFTSTREAM],
        selected: selectedShiftSources.includes(SHIFT_SOURCES.SHIFTSTREAM)
      },
      {
        value: SHIFT_SOURCES.EMAIL,
        label: SHIFT_SOURCE_LABELS[SHIFT_SOURCES.EMAIL],
        selected: selectedShiftSources.includes(SHIFT_SOURCES.EMAIL)
      }
    ],

    availabilityFilter: [
      {
        value: "MON",
        label: "Monday",
        selected: selectedWeekDays.includes("MON")
      },
      {
        value: "TUE",
        label: "Tuesday",
        selected: selectedWeekDays.includes("TUE")
      },
      {
        value: "WED",
        label: "Wednesday",
        selected: selectedWeekDays.includes("WED")
      },
      {
        value: "THU",
        label: "Thursday",
        selected: selectedWeekDays.includes("THU")
      },
      {
        value: "FRI",
        label: "Friday",
        selected: selectedWeekDays.includes("FRI")
      },
      {
        value: "SAT",
        label: "Saturday",
        selected: selectedWeekDays.includes("SAT")
      },
      {
        value: "SUN",
        label: "Sunday",
        selected: selectedWeekDays.includes("SUN")
      }
    ]
  };

  // ================= USE EFFECTS =================

  useEffect(() => {
    handlePartnerSelect(selectedPartnersIds);
    clearFilteredOptions(filters, setFilters, dropdownOptions);
  }, [selectedPartnersIds.length, JSON.stringify(selectedPartnersIds)]);

  useEffect(() => {
    setRoleIds(selectedRolesIds);
  }, [selectedRolesIds.length, JSON.stringify(selectedRolesIds)]);

  useEffect(() => {
    setVenueIds(selectedVenuesIds);
  }, [selectedVenuesIds.length, JSON.stringify(selectedVenuesIds)]);

  useEffect(() => {
    setHomeVenueIds(selectedHomeVenuesIds);
  }, [selectedHomeVenuesIds?.length, JSON.stringify(selectedHomeVenuesIds)]);

  useEffect(() => {
    setSelectedUserIds(selectedUsersIds);
  }, [selectedUsersIds.length, JSON.stringify(selectedUsersIds)]);

  useEffect(() => {
    setIsShowCancelledShifts(
      !!selectedShiftStatusIds.includes("CANCELLED_SHIFTS")
    );
  }, [selectedShiftStatusIds.length, JSON.stringify(selectedShiftStatusIds)]);

  useEffect(() => {
    setIsShowCancelledBookings(
      !!selectedShiftStatusIds.includes("CANCELLED_BOOKINGS")
    );
  }, [selectedShiftStatusIds.length, JSON.stringify(selectedShiftStatusIds)]);

  useEffect(() => {
    setShiftTypes(selectedShiftTypes);
  }, [selectedShiftTypes.length, JSON.stringify(selectedShiftTypes)]);

  useEffect(() => {
    setShiftSources(selectedShiftSources);
  }, [selectedShiftSources.length, JSON.stringify(selectedShiftSources)]);

  useEffect(() => {
    setCreatedAfter(selectedCreatedAfter);
  }, [selectedCreatedAfter.length, JSON.stringify(selectedCreatedAfter)]);

  useEffect(() => {
    setAvailabilityWeekDays(selectedWeekDays);
  }, [selectedWeekDays.length, JSON.stringify(selectedWeekDays)]);

  useEffect(() => {
    const preselectedFilters = getProvideScheduleFilters({
      id: currentUserId
    });

    if (preselectedFilters) {
      const baseFilters = GET_FILTERS(isSelfProvider).FILTERS;

      // set filter values from stored
      // but rectify for new filters etc.
      Object.entries(preselectedFilters).forEach(([key, value]) => {
        if (baseFilters[key]) {
          baseFilters[key].selectedItems = value.selectedItems;
        }
      });

      setFilters(baseFilters);
    }
  }, []);

  useEffect(() => {
    const filtersToSave = { ...filters };
    // Do not save searchTerm into local storage
    Object.keys(filtersToSave).forEach(
      filterKey =>
        (filtersToSave[filterKey] = {
          ...filtersToSave[filterKey],
          searchTerm: ""
        })
    );

    setProvideScheduleFilters({
      id: currentUserId,
      filters: filtersToSave
    });
  }, [filters]);

  // ================= RENDER =================
  return (
    <StyledFilterWrapper>
      <RotaDropdownHeader
        labelText={"Filter by..."}
        isOpen={openDropdowns.main}
        setIsOpen={() => toggleDropdown("main")}
        shouldShowDot={isAnyFilterSelected}
      >
        <StyledDropdownsContainer>
          {filtersArray.map(
            ({ name, displayName, searchPlaceholder }, index) => (
              <div key={name}>
                <StyledRotaDropdownHeader
                  labelText={displayName.toUpperCase()}
                  isOpen={openDropdowns[name]}
                  setIsOpen={() => toggleDropdown(name)}
                  shouldShowDot={!!filters[name].selectedItems.length}
                  opensToRight
                  onClose={() => handleSearch("", name)}
                  searchPlaceholder={searchPlaceholder}
                >
                  <RotaMultiSelectMenu
                    items={filterBySearchTerm(
                      dropdownOptions[name],
                      filters[name].searchTerm
                    )}
                    onSearch={value => handleSearch(value, name)}
                    onSelect={item => handleSelect(item, name)}
                    onClear={() => handleClear(name)}
                    showSearchInput={
                      ![
                        "shiftStatusFilter",
                        "shiftTypesFilter",
                        "shiftSourcesFilter",
                        "createdAfterFilter"
                      ].includes(name)
                    }
                  />
                </StyledRotaDropdownHeader>
                {!isLastItem(filtersArray, index) && <StyledSeparator />}
              </div>
            )
          )}
        </StyledDropdownsContainer>
      </RotaDropdownHeader>
      <StyledChipsContainer ref={chipsContainerRef}>
        <FilterChips
          filters={filters}
          handleClear={handleClear}
          setDropdownOpen={setDropdownOpen}
          containerWidth={chipsContainerRef?.current?.clientWidth}
        />
      </StyledChipsContainer>
    </StyledFilterWrapper>
  );
};

export default FilterDropdown;
